class ApiService {
  constructor() {
    // Uses the environment variable from your Dockerfile, 
    // and falls back to the relative path "/api" if not found.
    this.BASE_URL = process.env.REACT_APP_API_URL || "/api";
  }

  async get(endpoint) {
    try {
      const response = await fetch(`${this.BASE_URL}${endpoint}`);
      if (response.status === 402) {
        window.dispatchEvent(new CustomEvent('credits-depleted'));
        throw new Error('Insufficient AI Credits');
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`GET request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  async post(endpoint, data, isFormData = false) {
    try {
      const options = {
        method: "POST",
      };

      if (isFormData) {
        options.body = data;
      } else {
        options.headers = { "Content-Type": "application/json" };
        options.body = JSON.stringify(data);
      }

      const response = await fetch(`${this.BASE_URL}${endpoint}`, options);
      if (response.status === 402) {
        window.dispatchEvent(new CustomEvent('credits-depleted'));
        throw new Error('Insufficient AI Credits');
      }
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      const isFeature = ['/ai-consultant', '/leaf-disease', '/soil-classification', '/crop-recommendation', '/yield-prediction', '/satellite-data', '/manual-diagnosis', '/export-pdf'].some(p => endpoint.includes(p));
      if (isFeature) window.dispatchEvent(new Event('refresh-credits'));
      
      return await response.json();
    } catch (error) {
      console.error(`POST request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  async put(endpoint, data) {
    try {
      const response = await fetch(`${this.BASE_URL}${endpoint}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`PUT request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  async delete(endpoint, data = null) {
      try {
          const options = { method: "DELETE" };
          if (data) {
              options.headers = { "Content-Type": "application/json" };
              options.body = JSON.stringify(data);
          }
          const response = await fetch(`${this.BASE_URL}${endpoint}`, options);
          if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
          }
          return await response.json();
      } catch (error) {
          console.error(`DELETE request failed for ${endpoint}:`, error);
          throw error;
      }
  }

  /* Turn a non-PDF response into one readable sentence.
     The raw body goes to the console, never into the caller's message: a
     failed report can answer with the entire rendered HTML document, and
     alert()-ing 20KB of markup tells the user nothing. */
  async describePdfFailure(response, contentType) {
    let body = "";
    try {
      body = await response.text();
    } catch (e) {
      body = "";
    }
    console.error(
      `postPdf: expected application/pdf, got "${contentType || "no content-type"}" ` +
      `(HTTP ${response.status}). Body follows:`, body);

    const status = response.status;
    const clip = (s, n = 300) => {
      const flat = String(s).replace(/\s+/g, " ").trim();
      return flat.length > n ? flat.slice(0, n) + "..." : flat;
    };

    if (contentType.includes("json")) {
      try {
        const parsed = JSON.parse(body);
        // FastAPI puts the real cause in `detail`, as a string or a
        // validation-error list.
        const detail = parsed && parsed.detail;
        if (typeof detail === "string" && detail.trim()) {
          return `The server could not build the PDF (HTTP ${status}): ${clip(detail)}`;
        }
        if (Array.isArray(detail) && detail.length) {
          const fields = detail
            .map(d => (Array.isArray(d.loc) ? d.loc.join(".") : "") + (d.msg ? `: ${d.msg}` : ""))
            .filter(Boolean)
            .slice(0, 5)
            .join("; ");
          return `The server rejected the report data (HTTP ${status}): ${clip(fields)}`;
        }
        // 200 + JSON means the endpoint returned data where a PDF belongs.
        if (response.ok) {
          return "The server returned report data instead of a PDF file. " +
                 "This is a server misconfiguration, not a problem with your report - " +
                 "please report it to support.";
        }
        return `The server could not build the PDF (HTTP ${status}).`;
      } catch (e) {
        return `The server could not build the PDF (HTTP ${status}).`;
      }
    }

    if (contentType.includes("html") || body.trim().slice(0, 200).toLowerCase().includes("<!doctype html")) {
      return response.ok
        ? "The server returned an HTML page instead of a PDF file. The report was built " +
          "but not converted - please report this to support."
        : `The server returned an error page (HTTP ${status}). The report was not generated.`;
    }

    if (body.trim()) {
      return `The server could not build the PDF (HTTP ${status}): ${clip(body, 200)}`;
    }
    return `The server could not build the PDF (HTTP ${status}).`;
  }

  async postPdf(endpoint, data) {
    let response;
    try {
      response = await fetch(`${this.BASE_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/pdf" },
        body: JSON.stringify(data)
      });
    } catch (error) {
      console.error(`POST PDF request failed for ${endpoint}:`, error);
      throw new Error("Could not reach the server. Check your connection and try again.");
    }

    /* Assert we actually got a PDF. A 200 is not enough: the endpoint has
       shipped a JSON-serialised HTML string on a 200 before, and the caller
       would happily hand that to the user as a .pdf download. */
    const contentType = (response.headers.get("content-type") || "").toLowerCase();
    if (!response.ok || !contentType.includes("application/pdf")) {
      throw new Error(await this.describePdfFailure(response, contentType));
    }

    const blob = await response.blob();
    if (blob.size === 0) {
      throw new Error("The server returned an empty PDF file. Please try again.");
    }
    return blob;
  }
}

export default new ApiService();
